import PropTypes from 'prop-types'
import { MdFlight, MdLocationOn, MdPhone, MdEmail, MdLanguage, MdBusiness } from 'react-icons/md'
import { formatCurrency, getStatusColor, extractDomain, truncateText } from '../../utils/airline'

function AirlineCard({ airline, onClick, className = '' }) {
  return (
    <div
      className={`bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 hover:shadow-lg dark:hover:shadow-lg transition-all duration-200 cursor-pointer ${className}`}
      onClick={onClick}
    >
      <div className="flex items-start space-x-4">
        {/* Airline Logo */}
        <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
          {airline.logo ? (
            <img
              src={airline.logo}
              alt={`${airline.name} logo`}
              className="w-12 h-12 rounded object-cover"
              onError={(e) => {
                e.target.style.display = 'none'
                e.target.nextSibling.style.display = 'flex'
              }}
            />
          ) : null}
          <MdFlight
            className="text-white text-2xl"
            style={{ display: airline.logo ? 'none' : 'flex' }}
          />
        </div>

        {/* Airline Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white truncate">
                {airline.name}
              </h3>
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                  {airline.iataCode} / {airline.icaoCode}
                </span>
                {airline.callsign && (
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">
                    {airline.callsign}
                  </span>
                )}
              </div>
            </div>
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(airline.status)}`}
            >
              {airline.status}
            </span>
          </div>

          {/* Key Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400">
                <MdLocationOn className="text-neutral-500" />
                <span>{airline.country}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400">
                <MdBusiness className="text-neutral-500" />
                <span>Fleet: {airline.fleetSize || 0} aircraft</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400">
                <MdFlight className="text-neutral-500" />
                <span>{airline.destinations?.length || 0} destinations</span>
              </div>
            </div>

            <div className="space-y-2">
              {airline.contactInfo?.customerService?.phone && (
                <div className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400">
                  <MdPhone className="text-neutral-500" />
                  <span className="truncate">{airline.contactInfo.customerService.phone}</span>
                </div>
              )}
              {airline.contactInfo?.customerService?.email && (
                <div className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400">
                  <MdEmail className="text-neutral-500" />
                  <span className="truncate">
                    {truncateText(airline.contactInfo.customerService.email, 25)}
                  </span>
                </div>
              )}
              {airline.website && (
                <div className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400">
                  <MdLanguage className="text-neutral-500" />
                  <span className="truncate">{extractDomain(airline.website)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Financial Info */}
          {airline.financialInfo?.annualRevenue && (
            <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Annual Revenue ({airline.financialInfo.currency})
                </span>
                <span className="text-lg font-semibold text-neutral-900 dark:text-white">
                  {formatCurrency(
                    airline.financialInfo.annualRevenue,
                    airline.financialInfo.currency,
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

AirlineCard.propTypes = {
  airline: PropTypes.shape({
    name: PropTypes.string.isRequired,
    code: PropTypes.string,
    country: PropTypes.string,
    logo: PropTypes.string,
    website: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    status: PropTypes.string,
    iataCode: PropTypes.string,
    icaoCode: PropTypes.string,
    callsign: PropTypes.string,
    founded: PropTypes.string,
    headquarters: PropTypes.object,
    fleetSize: PropTypes.number,
    destinations: PropTypes.array,
    hubAirports: PropTypes.array,
    alliance: PropTypes.string,
    frequentFlyerProgram: PropTypes.object,
    contactInfo: PropTypes.object,
    financialInfo: PropTypes.object,
    regulatoryInfo: PropTypes.object,
    socialMedia: PropTypes.object,
  }).isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
}

export default AirlineCard
